import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import { TextElement } from './elements/text-element';
import { SingleLineElement } from './elements/single-line-element';
import { MultilineElement } from './elements/multiline-element';
import { NumberElement } from './elements/number-element';
import { PhoneElement } from './elements/phone-element';
import { EmailElement } from './elements/email-element';
import { FullNameElement } from './elements/full-name-element';
import { AddressElement } from './elements/address-element';
import { DateElement } from './elements/date-element';
import { TimeElement } from './elements/time-element';
import { DropdownElement } from './elements/dropdown-element';
import { CheckboxElement } from './elements/checkbox-element';
import { RadiobuttonElement } from './elements/radiobutton-element';
import { LinkElement } from './elements/link-element';
import { RatingElement } from './elements/rating-element';
import { SignatureElement } from './elements/signature-element';
import { TableElement } from './elements/table-element';
import { SingleLineHeaderElement } from './elements/single-line-header-element';
import { SubmittersFieldElement } from './elements/submitters-field-element';
import { FileUploadElement } from './elements/file-upload-element';
import { FileAttachElement } from './elements/file-attach-element';
import { DividerElement } from './elements/divider';
import { LoginElement } from './elements/login-element';
import { CreditsElement } from './elements/credits-element';
import { getAlignedLeft, measureFieldWidth, measureTableWidth } from './elements-settings/components/field-alignment-setting';
import { getBuilderZIndex } from '../../utility/alwaysOnTop';
import cx from 'classnames';
import { GridYValue, GridXUnit, GridYUnit } from '../../constants';
import useOutsideClick from '../../hook/useOutsideClick';

// Field types resized by the width handle alone, drawn without an input box of
// their own. They get an outlined box on the canvas so there is an edge to see
// the width by and to drag — see `.is-resize-boxed` in form-builder/layout.scss.
const WIDTH_BOXED_TYPES = ['submitters-field', 'checkbox'];

// Those of them that ship without a default `options.width`, so an existing one
// has to be measured on the canvas before it can be dragged.
const WIDTH_BACKFILL_TYPES = ['submitters-field', 'checkbox'];

// Field types drawn at the height of their own content rather than at the
// stored `options.height`, so the box always contains what is inside it.
// Credits has no height handle, and the 13 rows it ships with left a strip of
// empty box under its input.
const HEIGHT_FROM_CONTENT_TYPES = ['checkbox', 'text', 'radiobutton', 'table', 'submitters-field', 'credits'];

// Of those, the ones that store the height they ended up at, so the printed
// form reserves the same room the canvas showed. `text` is left out: it sizes
// itself from its own editor.
const HEIGHT_WRITE_BACK_TYPES = ['checkbox', 'table', 'radiobutton', 'submitters-field', 'credits'];

// Field types whose hover / selected frame is drawn inside the box's padding,
// closer to the field's content, instead of on the box's own edge. Only the
// frame moves: the box keeps its size and padding, which the stored height and
// the printed form are measured from. See `.has-inset-frame` in
// form-builder/layout.scss.
const INSET_FRAME_TYPES = [
  'single-line',
  'multiline-line',
  'number',
  'address',
  'name',
  'email',
  'phone',
  'date',
  'time',
  'dropdown',
  'link',
  'table',
  'file-attach',
  'radiobutton',
  'checkbox',
  'file-upload',
  'single-line-header',
  'signature',
  'login',
];

// Those drawn in further still: their content leaves the most room in the box.
const DEEP_INSET_FRAME_TYPES = ['credits', 'rating'];

// The coordinates a drop has to store, as [value, key] pairs. Both edges of the
// canvas are 0, and 0 is falsy: testing the dropped coordinates for truth threw
// away exactly the drops that landed against the top and the left edge, leaving
// the field drawn where it was dropped but saved where it came from. A drag that
// never moved the field has no coordinates at all, and still stores nothing.
export const getDropUpdates = (newPosition) =>
  ['left', 'top'].filter((key) => typeof newPosition?.[key] === 'number').map((key) => [newPosition[key], key]);

export const FromBuilderItem = function FromElement(props) {
  const {
    id,
    element,
    handlerRemove,
    handlerSettings,
    isActive,
    isOverTextField,
    moveCard,
    index,
    findCard,
    handlerChangeOption,
    handlerCloseSettings,
    handlerChangeWidth,
    duplicateElement,
    handleChangeTableColumn,
    autoFillingVariables,
    addAutoFillingVariable,
  } = props;
  const originalIndex = findCard(id).index;
  const previewContainerRef = useRef();

  const positionValues = useMemo(() => {
    const data = {
      left: element.options.left,
      top: element.options.top,
      width: element.options.width,
      innerElemHeight: element.options.innerElemHeight,
      height: element.options.height,
      startWidth: element.options.width,
      startHeight: element.options.height,
      startInnerElemHeight: element.options.innerElemHeight,
    };

    return data;
  }, [element.options.left, element.options.width, element.options.top, element.options.innerElemHeight, element.options.height]);

  const [position, setPosition] = useState(positionValues);
  const [fieldIsActive, setFieldIsActive] = useState(false);

  const positionData = useMemo(() => {
    let incrementheight = 0;

    if (element.type === 'table' && !!element.options.addRowsButton) {
      incrementheight += 5;
    }

    return {
      left: position.left - 2 + GridXUnit,
      top: (position.top - 2) * GridYValue + GridYUnit,
      innerElemHeight: position.innerElemHeight * GridYValue + GridYUnit,
      width: position.width + 4 + GridXUnit,
      height: (position.height + incrementheight) * GridYValue + GridYUnit,
    };
  }, [JSON.stringify(position), element.options.addRowsButton]);

  // Takes on a height the form got from anywhere else (a measured box, a save
  // coming back, a resize of this very field written to the form), and records
  // the baseline that came with it, so the write-back below can tell it from a
  // resize of its own. Recorded in the same state as the baseline, not in a ref:
  // the two have to change together, render for render.
  useEffect(() => {
    setPosition((values) => ({
      ...values,
      height: element.options.height,
      startHeight: element.options.height,
      syncedStartHeight: element.options.height,
      syncedStartInnerElemHeight: values.startInnerElemHeight,
    }));
  }, [element.options.height]);

  useEffect(() => {
    setPosition((values) => ({
      ...values,
      width: element.options.width,
      startWidth: element.options.width,
      left: element.options.left,
    }));
  }, [element.options.size]);

  // Keeps the canvas in sync when `left` is changed from outside the canvas
  // (e.g. the Field Alignment setting). During a drag `options.left` is only
  // written on drop, so this re-runs with the value the local state already has.
  useEffect(() => {
    setPosition((values) => ({ ...values, left: element.options.left }));
  }, [element.options.left]);

  // Same for `width`, which alignment fills in for the fields that never had
  // one. `startWidth` is left alone so this does not look like a resize, except
  // when there is none yet: a field getting its first width needs the resize
  // baseline seeded, or the first drag would compute its new width from NaN.
  useEffect(() => {
    setPosition((values) => ({
      ...values,
      width: element.options.width,
      startWidth: typeof values.startWidth === 'number' ? values.startWidth : element.options.width,
    }));
  }, [element.options.width]);

  const handleEndDrag = useCallback((item) => {
    getDropUpdates(item.newPosition).forEach(([value, key]) => handlerChangeOption(value, key, element.id));

    item.dragStartScrollY = undefined;
  });

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: 'fromElement',
      item: {
        id,
        originalIndex,
        positionValues,
        position,
        setPosition,
        areaWidth: document.getElementsByClassName('drop-area')[0].offsetWidth,
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: handleEndDrag,
    }),
    [id, originalIndex, moveCard, isActive],
  );

  useEffect(() => {
    preview(getEmptyImage());
  }, []);

  // Writes the resized width back to the form. Mounting seeds `startWidth` from
  // the stored width, so skip that first pass: it is not a resize, and letting
  // it through made every field re-run its options on load.
  useEffect(() => {
    if (position.width === element.options.width) return;

    handlerChangeOption(position.width, 'width', element.id);
  }, [position.startWidth]);

  // An aligned field that shrinks to fit its content (Submitters info auto
  // entry and the other types with no default width) has no width to be placed
  // against, so its alignment was stored but never applied. Measure it on the
  // canvas and hand the width over: the `width` change re-runs the alignment,
  // and from then on the field has a width like any other, so this runs once.
  useEffect(() => {
    if (!element.options.fieldAlignment) return;

    const measuredWidth = measureFieldWidth(element);
    if (measuredWidth === null) return;

    handlerChangeOption(measuredWidth, 'width', element.id);
  }, [element.options.fieldAlignment, element.options.width]);

  // A table is aligned by the width it is drawn at, which changes with its
  // columns, rows and question column without any `width` write to re-run the
  // alignment. So it is checked against every change to the table, and placed
  // again only once it has actually come out of line.
  useEffect(() => {
    if (element.type !== 'table' || !element.options.fieldAlignment) return;

    const alignedLeft = getAlignedLeft(element.options.fieldAlignment, measureTableWidth(element));
    if (alignedLeft === null || alignedLeft === element.options.left) return;

    handlerChangeOption(element.options.fieldAlignment, 'fieldAlignment', element.id);
  }, [JSON.stringify(element)]);

  // Submitters info auto entry and the checkbox list never got a default width,
  // so they shrank to fit their content and the resize handle had no width to
  // drag from. Pin the width they are already rendering at, once, so they become
  // resizable like every other field without changing how they look until
  // someone actually drags them.
  useEffect(() => {
    if (!WIDTH_BACKFILL_TYPES.includes(element.type)) return;
    if (typeof element.options.width === 'number') return;

    const measuredWidth = measureFieldWidth(element);
    if (measuredWidth === null) return;

    handlerChangeOption(measuredWidth, 'width', element.id);
  }, [element.type, element.options.width]);

  // Writes the height a resize ended at to the form. A baseline the form itself
  // just set (above) is not a resize, and is left alone: when the form's height
  // changed again before the last one had been taken on, writing it back put the
  // old height over the new one, which was then taken on and written back in
  // turn - the two swapped places every render, and every render saved the form.
  // That was the endless stream of saves, and the page shaking with them, after
  // a quick resize or on opening some forms.
  useEffect(() => {
    if (position.startHeight === position.syncedStartHeight && position.startInnerElemHeight === position.syncedStartInnerElemHeight) {
      return;
    }

    handlerChangeOption(position.height, 'height', element.id);
    handlerChangeOption(position.innerElemHeight, 'innerElemHeight', element.id);
  }, [position.startHeight, position.startInnerElemHeight]);

  useOutsideClick(previewContainerRef, () => setFieldIsActive(false));

  useEffect(() => {
    const updateAdditionalTop = () => {
      const elem = previewContainerRef?.current?.firstChild;
      if (elem?.offsetHeight) {
        if (element.type === 'text') {
        } else if (HEIGHT_WRITE_BACK_TYPES.includes(element.type)) {
          const realHeight = Math.ceil(elem.offsetHeight / GridYValue);
          handlerChangeOption(realHeight, 'height', element.id);
        }
      }
      return 0;
    };

    const resizeObserver = new ResizeObserver(updateAdditionalTop);
    if (previewContainerRef?.current?.firstChild) {
      resizeObserver.observe(previewContainerRef?.current?.firstChild);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [handlerChangeOption, JSON.stringify(element)]);

  return (
    <>
      <div ref={previewContainerRef}>
        <div
          onClick={() => setFieldIsActive(true)}
          ref={drag}
          className={cx(
            `form-builder-element-preview form-builder-element-${element.id} form-label-alignment-${element.options.alignment} form-label-${element.options.size}`,
            {
              'form-builder-element-preview-drag': isDragging,
              'form-builder-element-preview-active': fieldIsActive,
              'form-builder-element-preview-settings-active': isActive,
              'is-divider': element.type === 'divider',
              'is-over-text-field': isOverTextField,
              'has-inset-frame': INSET_FRAME_TYPES.includes(element.type),
              'has-deep-inset-frame': DEEP_INSET_FRAME_TYPES.includes(element.type),
            },
          )}
          style={{
            position: 'absolute',
            left: positionData.left,
            top: positionData.top,
            width: positionData.width,
            height: HEIGHT_FROM_CONTENT_TYPES.includes(element.type) ? 'unset' : positionData.height,
            zIndex: isDragging ? 99999999 : getBuilderZIndex(element),
          }}
        >
          <div
            className={cx('form-builder-element-preview-wrapper', {
              'is-resize-boxed': WIDTH_BOXED_TYPES.includes(element.type),
              'is-submitters-field': element.type === 'submitters-field',
              'is-signature-field': element.type === 'signature',
            })}
            style={{
              width: '100%',
              height: '100%',
            }}
          >
            {element.type === 'text' && (
              <>
                <TextElement
                  element={element}
                  isActive={isActive}
                  handlerChangeOption={handlerChangeOption}
                  handlerCloseSettings={handlerCloseSettings}
                  setPosition={setPosition}
                  innerElemHeight={positionData.innerElemHeight}
                  autoFillingVariables={autoFillingVariables}
                  addAutoFillingVariable={addAutoFillingVariable}
                />
              </>
            )}
            {element.type === 'single-line' && (
              <SingleLineElement
                element={element}
                setPosition={setPosition}
                innerElemHeight={positionData.innerElemHeight}
                handlerChangeOption={handlerChangeOption}
                autoFillingVariables={autoFillingVariables}
                addAutoFillingVariable={addAutoFillingVariable}
              />
            )}
            {element.type === 'multiline-line' && (
              <MultilineElement
                element={element}
                handlerChangeOption={handlerChangeOption}
                setPosition={setPosition}
                innerElemHeight={positionData.innerElemHeight}
              />
            )}
            {element.type === 'number' && (
              <NumberElement element={element} setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} />
            )}
            {element.type === 'phone' && (
              <PhoneElement element={element} setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} />
            )}
            {element.type === 'email' && (
              <EmailElement element={element} setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} />
            )}
            {element.type === 'name' && <FullNameElement element={element} />}
            {element.type === 'address' && <AddressElement element={element} setPosition={setPosition} />}
            {element.type === 'time' && (
              <TimeElement element={element} setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} />
            )}
            {element.type === 'date' && (
              <DateElement element={element} setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} />
            )}
            {element.type === 'dropdown' && (
              <DropdownElement element={element} setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} />
            )}
            {element.type === 'credits' && <CreditsElement element={element} handlerChangeOption={handlerChangeOption} />}

            {element.type === 'checkbox' && (
              <CheckboxElement element={element} innerElemHeight={positionData.innerElemHeight} setPosition={setPosition} />
            )}
            {element.type === 'radiobutton' && <RadiobuttonElement element={element} innerElemHeight={positionData.innerElemHeight} />}
            {element.type === 'link' && <LinkElement element={element} />}
            {element.type === 'rating' && <RatingElement element={element} innerElemHeight={positionData.innerElemHeight} />}
            {element.type === 'signature' && (
              <SignatureElement element={element} setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} />
            )}
            {element.type === 'table' && (
              <TableElement
                element={element}
                handlerChangeWidth={handlerChangeWidth}
                handleChangeTableColumn={handleChangeTableColumn}
                isActive={isActive}
                innerElemHeight={positionData.innerElemHeight}
              />
            )}
            {element.type === 'single-line-header' && (
              <SingleLineHeaderElement
                element={element}
                handlerChangeOption={handlerChangeOption}
                setPosition={setPosition}
                innerElemHeight={positionData.innerElemHeight}
              />
            )}
            {element.type === 'submitters-field' && <SubmittersFieldElement element={element} setPosition={setPosition} />}
            {element.type === 'file-upload' && (
              <FileUploadElement setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} element={element} />
            )}
            {element.type === 'file-attach' && <FileAttachElement handelChange={handlerChangeOption} element={element} />}
            {element.type === 'login' && (
              <LoginElement element={element} setPosition={setPosition} innerElemHeight={positionData.innerElemHeight} />
            )}
            {element.type === 'divider' && <DividerElement element={element} />}
          </div>
          <div
            // data-handler-id={handlerId}
            className={cx('form-builder-element-preview-action')}
            style={{
              left: element.type === 'divider' ? '-50px' : '100%',
              display: element.type === 'divider' ? 'grid' : 'block',
            }}
          >
            <button onClick={() => duplicateElement(element.id)} className="btn btn-settings btn-icon">
              <i className="icon-copy"></i>
            </button>
            <button onClick={() => handlerSettings(element)} className="btn btn-settings btn-icon">
              <i className="icon-settings"></i>
            </button>
            <button onClick={() => handlerRemove(element, isActive)} className="btn btn-remove btn-icon">
              <i className="icon-remove"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
